import { omit } from 'lodash';
import React, { useEffect, useState } from 'react';
import { ChartFragment } from 'models/api-response';
import { DosageTime, MarRecord, SaveOptions } from 'models/ui';
import { NAV_ID } from 'constants/app.constant';
import Box from 'components/common/box/Box';
import { ELSButton } from 'components/common/els';
import Sidebar from 'components/common/sidebar/Sidebar';
import { HOLD_UNHOLD_MODE } from './constants';
import MarHoldUnholdItem, { HoldUnholdDosageItem } from './MarHoldUnholdItem';
import { buildUnholdFragment, getHoldableDosageTimes } from './helper';
import './mar.hold.sidebar.scss';

// Held dosages arrive pre-checked so unchecking is an explicit "keep held" decision;
// holdable dosages start unchecked so holding is an explicit opt-in.
const buildSelection = (record: MarRecord, isHoldMode: boolean): HoldUnholdDosageItem[] => {
  const dosageTimes: DosageTime[] = isHoldMode ? getHoldableDosageTimes(record) : (record.dosageTimes ?? []).filter((dosageTime) => dosageTime.isHeld);
  return dosageTimes.map((dosageTime) => ({ ...dosageTime, checked: !isHoldMode }));
};

const toDosageTime = (item: HoldUnholdDosageItem): DosageTime => omit(item, 'checked');

interface MarHoldUnholdSidebarProps {
  record: MarRecord;
  mode: string;
  isOpen: boolean;
  onCloseSidebar: Function;
  onContinueHold: Function;
  fragments: ChartFragment[];
  presaveFragment: Function;
  saveChart: <T>(fragments: Array<Partial<ChartFragment>>, options?: SaveOptions) => Promise<T>;
  onUpdate: Function;
}

const MarHoldUnholdSidebar = ({ record, mode, isOpen, onCloseSidebar, onContinueHold, fragments, presaveFragment, saveChart, onUpdate }: MarHoldUnholdSidebarProps) => {
  const [selection, setSelection] = useState<HoldUnholdDosageItem[]>([]);
  const isHoldMode = mode === HOLD_UNHOLD_MODE.HOLD;

  useEffect(() => {
    setSelection(isOpen && record ? buildSelection(record, isHoldMode) : []);
  }, [isOpen, record, isHoldMode]);

  const checkedDosages = selection.filter((item) => item.checked);

  const handleToggle = (dosageItem: HoldUnholdDosageItem) => {
    setSelection((previous) => previous.map((item) => (item.id === dosageItem.id ? { ...item, checked: !item.checked } : item)));
  };

  const handleUnhold = () => {
    const fragment = fragments.find((item) => item.fragmentId === record.fragmentId);
    const dosageIds = checkedDosages.map((item) => item.id);
    saveChart([buildUnholdFragment(fragment, dosageIds, presaveFragment)], { navId: NAV_ID.PHARMACY, afterSave: onUpdate });
  };

  const message = isHoldMode ? 'Select the dosage times you want to hold.' : 'Select the dosage times you want to unhold. Unchecked dosage times remain held.';
  const medicationSummary = [record?.route, record?.dose, record?.frequency].filter(Boolean).join(' · ');

  return (
    <Sidebar className="mar-hold-unhold-sidebar-box" open={isOpen} onClose={onCloseSidebar}>
      <Box mt3 mb2>
        <h2>{isHoldMode ? 'Hold' : 'Unhold'}</h2>
      </Box>
      <Box className="mar-hold-unhold-sidebar">
        <div className="mar-hold-unhold-sidebar__message">{message}</div>
        <div className="mar-hold-unhold-sidebar__header">
          <h3>{record?.drugName}</h3>
          {medicationSummary && <div className="mar-hold-unhold-sidebar__header__details">{medicationSummary}</div>}
          {record?.specialInstructions && <div className="mar-hold-unhold-sidebar__header__more">{record.specialInstructions}</div>}
        </div>
        <div className="mar-hold-unhold-sidebar__list">
          {selection.map((item) => (
            <MarHoldUnholdItem key={item.id} dosageItem={item} isSingle={selection.length === 1} onToggle={handleToggle} />
          ))}
        </div>
        {isHoldMode ? (
          <ELSButton
            className="action-btn"
            iconSize="1x"
            iconName="chevron-right"
            iconPosition="right"
            text="Continue"
            onClick={() => onContinueHold(checkedDosages.map(toDosageTime))}
            isDisabled={checkedDosages.length === 0}
          />
        ) : (
          <ELSButton className="action-btn" text="Unhold" onClick={handleUnhold} isDisabled={checkedDosages.length === 0} />
        )}
      </Box>
    </Sidebar>
  );
};

MarHoldUnholdSidebar.displayName = 'MarHoldUnholdSidebar';
export default MarHoldUnholdSidebar;
